import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactCardFlip from 'react-card-flip';
import { updateStudyStreak, saveLearnedWord } from '../utils/storage';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/BackButton';
import './styles/FlashCard.css';

const Flashcard = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { subsetflashcards, selectedChapter, vocabIndex } = location.state || {};
  const getAns = (item) => (i18n.language !== 'en' && item[i18n.language]) || item.answer;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const currentFlashcard = subsetflashcards[currentIndex];
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    updateStudyStreak();
  }, []);

  useEffect(() => {
    if (currentFlashcard) {
      saveLearnedWord(
        currentFlashcard.id,
        currentFlashcard.question,
        currentFlashcard.answer,
        selectedChapter?.name,
        currentFlashcard.ru ? { ru: currentFlashcard.ru } : {}
      );
    }
  }, [currentIndex]);

  const speakNow = (textToRead) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(textToRead);
    utterance.lang = "fi-FI";
    synth.speak(utterance);
  };

  const showNextCard = () => {
    setIsFlipped(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % subsetflashcards.length);
  };

  const showPreviousCard = () => {
    setIsFlipped(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? subsetflashcards.length - 1 : prevIndex - 1
    );
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touch = e.changedTouches ? e.changedTouches[0] : e;
    const diffX = touch.clientX - touchStartX.current;
    const diffY = Math.abs(touch.clientY - touchStartY.current);
    const minSwipeDistance = 50;

    if (Math.abs(diffX) > minSwipeDistance && Math.abs(diffX) > diffY) {
      if (diffX > 0) {
        showPreviousCard();
      } else {
        showNextCard();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') showPreviousCard();
      else if (e.key === 'ArrowRight') showNextCard();
      else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFlip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const progress = ((currentIndex + 1) / subsetflashcards.length) * 100;

  return (
    <div className="container">
      <BackButton label={selectedChapter?.name || 'Back'} />

      <div className="flashcard-header">
        <h2 className="flashcard-title">Vocabulary {vocabIndex}</h2>
        <span className="flashcard-counter">{currentIndex + 1} / {subsetflashcards.length}</span>
      </div>

      <div className="flashcard-progress-bar">
        <div className="flashcard-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div
        className="contentContainer"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseUp={handleTouchEnd}
      >
        <ReactCardFlip type="vertical" isFlipped={isFlipped}>
          <div className="flashcardContainer flashcardFront" onClick={handleFlip}>
            <p className="flashcardText">{currentFlashcard.question}</p>
            <span className="flashcard-hint">Tap to flip</span>
          </div>
          <div className="flashcardContainer flashcardBack" onClick={handleFlip}>
            <p className="flashcardAnswer">{getAns(currentFlashcard)}</p>
          </div>
        </ReactCardFlip>

        <div className="flashcard-controls">
          <button onClick={showPreviousCard} className="flashcard-nav-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            onClick={() => speakNow(currentFlashcard.question)}
            className="flashcard-speak-btn"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          </button>
          <button onClick={showNextCard} className="flashcard-nav-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Flashcard;

